import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { MutationRefetchQueries, ValidationError } from "~/common/types";
import {
  GetDataSourceForEditFormDocument,
  UpdateDataSourceDocument,
  type UpdateDataSourceMutation,
  type UpdateDataSourceMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { DataSourceSheetIdEditFormData } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseDataSourceSheetIdEditParams {
  onSubmit: () => void;
  onError: (error: Pick<ValidationError, "fields">) => void;
  dataSourceId: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
}

export const useDataSourceSheetIdEdit = ({
  onSubmit,
  onError,
  dataSourceId,
  refetchQueriesOnEdit,
}: UseDataSourceSheetIdEditParams) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const [updateDataSource] = useMutation<UpdateDataSourceMutation, UpdateDataSourceMutationVariables>(
    UpdateDataSourceDocument,
    {
      refetchQueries: [
        { query: GetDataSourceForEditFormDocument, variables: { tenantId, id: dataSourceId } },
        ...refetchQueriesOnEdit,
      ],
    },
  );

  const submit = useCallback(
    (formData: DataSourceSheetIdEditFormData) => {
      const progressToastId = toast.loading(t("ui.processing"));
      setProcessing(true);

      updateDataSource({
        variables: {
          input: {
            tenantId,
            id: dataSourceId,
            googleSheetId: Number(formData.sheetId),
          },
        },
      })
        .then((resp) => {
          switch (resp.data?.cnutUpdateDataSource.__typename) {
            case "DataSourceUpdateOutput":
              onSubmit();
              toast.success(
                t("feature.common.updatedSuccessfully", {
                  model: "dataSource",
                }),
              );
              break;
            case "ValidationError":
              onError(resp.data.cnutUpdateDataSource);
              break;
            default:
            // do nothing
          }
        })
        .catch((e: unknown) => {
          if (e instanceof Error) {
            toast.error(translateError(t, e));
          }
        })
        .finally(() => {
          toast.dismiss(progressToastId);
          setProcessing(false);
        });
    },
    [updateDataSource, dataSourceId, onSubmit, onError, t, tenantId],
  );

  return {
    submit,
    processing,
  };
};
